import { DialogContent, DialogOverlay } from "@reach/dialog";
import { AnimatePresence, motion } from "framer-motion";
import { atom, useSetAtom } from "jotai";
import { useState } from "react";

import { Box, CloseIcon, IconButton, getThemeValue } from "@ms/gatsby-theme";

import { overlayVariants } from "@theme/components/Modal/variants";

import type { ThemeUIStyleObject } from "@ms/gatsby-theme";
import type { FC, ReactNode } from "react";

const contentVariant = {
  initial: {
    x: 100,
  },
  animate: {
    x: 0,
    transition: { min: 0, max: 100, bounceDamping: 9 },
  },
  exit: {
    x: 100,
    transition: { min: 0, max: 100, bounceDamping: 9 },
  },
};

/**
 * Flyout Cart Open Atom
 *
 * Whether the flyout cart is open
 */
export const flyoutCartOpen = atom(false);

export interface FlyoutModalProps {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  "aria-label": string;
  overlayStyles?: ThemeUIStyleObject;
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export interface FlyoutModalComponents {
  Header: FC<FlyoutModalHeaderProps>;
  Back: FC<FlyoutModalBackProps>;
  CartBack: FC<FlyoutCartModalBackProps>;
  Content: FC<FlyoutModalContentProps>;
  StickyBar: FC<FlyoutModalStickyBarProps>;
}

const FlyoutDialogOverlay = motion(DialogOverlay);
const FlyoutDialogContent = motion(DialogContent);

export const FlyoutModal: FC<FlyoutModalProps> & FlyoutModalComponents = ({
  isOpen,
  close,
  overlayStyles,
  sx,
  className,
  // unused variables but don't want them in ...rest
  open, // eslint-disable-line @typescript-eslint/no-unused-vars
  toggle, // eslint-disable-line @typescript-eslint/no-unused-vars
  children,
  ...rest
}) => {
  const [isDoneAnimating, setIsDoneAnimating] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <FlyoutDialogOverlay
          isOpen={isOpen}
          onDismiss={close}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={overlayVariants}
          dangerouslyBypassFocusLock={!isDoneAnimating}
          sx={{
            variant: "modals.flyoutOverlay",
            scrollPadding: "100px 0 200px",
            ...overlayStyles,
          }}
        >
          <FlyoutDialogContent
            initial="initial"
            animate="animate"
            exit="exit"
            variants={contentVariant}
            onAnimationComplete={(status) => setIsDoneAnimating(status === "animate")}
            sx={{
              variant: "modals.flyoutContent",
              maxWidth: ["none", null, 400],
              height: "full",
              maxHeight: "100%",
              "&&": {
                width: "100vw ",
              },
              ...sx,
            }}
            className={className}
            {...rest}
          >
            {children}
          </FlyoutDialogContent>
        </FlyoutDialogOverlay>
      )}
    </AnimatePresence>
  );
};

interface FlyoutModalHeaderProps {
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export const FlyoutModalHeader: FC<FlyoutModalHeaderProps> = ({ sx, className, children }) => (
  <Box
    sx={{
      bg: "goldDark",
      color: "white",
      textAlign: "center",
      py: "s500",
      position: "sticky",
      zIndex: 10,
      top: 0,
      left: 0,
      right: 0,
      ...sx,
    }}
    className={className}
  >
    {children}
  </Box>
);

interface FlyoutCartModalBackProps {}
interface FlyoutModalBackProps {
  close: () => void;
  ariaLabel?: string;
}

interface CloseButtonProps {
  close: () => void;
  ariaLabel: string;
}
const CloseButton: FC<CloseButtonProps> = ({ close, ariaLabel }) => (
  <IconButton
    variant="iconDark"
    sx={{
      display: "block",
      width: "auto",
      height: "auto",
      p: "s300",
      m: 0,
      position: "absolute",
      top: "50%",
      right: "s200",
      transform: "translateY(-50%)",
    }}
    onClick={() => close()}
    aria-label={ariaLabel}
  >
    <CloseIcon sx={{ width: "iconSizeBase", height: "auto" }} />
  </IconButton>
);

export const FlyoutCartModalBack: FC<FlyoutCartModalBackProps> = () => {
  const setIsFlyoutCartOpen = useSetAtom(flyoutCartOpen);

  return <CloseButton close={() => setIsFlyoutCartOpen(false)} ariaLabel="Close cart" />;
};

export const FlyoutModalBack: FC<FlyoutModalBackProps> = ({ close, ariaLabel }) => (
  <CloseButton close={close} ariaLabel={ariaLabel ?? "Close"} />
);

interface FlyoutModalContentProps {
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export const FlyoutModalContent: FC<FlyoutModalContentProps> = ({ sx, className, children }) => (
  <Box sx={{ bg: "white", position: "relative", ...sx }} className={className}>
    {children}
  </Box>
);

interface FlyoutModalStickyBarProps {
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}
export const FlyoutModalStickyBar: FC<FlyoutModalStickyBarProps> = ({
  sx,
  className,
  children,
}) => (
  <Box
    className={className}
    sx={{
      position: "sticky",
      zIndex: 10,
      left: 0,
      right: 0,
      bottom: 0,
      bg: "white",
      borderTop: (t) => `1px solid ${getThemeValue(t, "colors.grey100")}`,
      px: ["s500", null, null, "s600"],
      ...sx,
    }}
  >
    {children}
  </Box>
);

FlyoutModal.Header = FlyoutModalHeader;
FlyoutModal.Back = FlyoutModalBack;
FlyoutModal.CartBack = FlyoutCartModalBack;
FlyoutModal.Content = FlyoutModalContent;
FlyoutModal.StickyBar = FlyoutModalStickyBar;
